import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, H4, InputModal, P } from '../../lib/components'
import { displayDollar } from '../../lib/utils/helpers'
import { investServices } from '../../services'
import { RootState } from '../../store'
import './cancel-investment-modal.scss'

interface CancelInvestmentModalProps {
  isOpen: boolean
  onClose: () => void
  investmentId: number
  opportunityId: number
  projectName: string
  investmentAmount: string
  currencyCode: string
  /** The order was already paid from the wallet, so cancelling returns the money. */
  refundsToWallet?: boolean
  /** What goes back to the wallet when it differs from the order amount (share orders include the fee). */
  refundAmount?: string
  onCancelSuccess: () => void
}

export const CancelInvestmentModal: React.FC<CancelInvestmentModalProps> = ({
  isOpen,
  onClose,
  investmentId,
  opportunityId,
  projectName,
  investmentAmount,
  currencyCode,
  refundsToWallet = false,
  refundAmount: refundAmountProp,
  onCancelSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const authToken = useSelector((state: RootState) => state.investor?.authToken)
  const userId = useSelector((state: RootState) => state.investor?.userId)

  const refundAmount = displayDollar(refundAmountProp ?? investmentAmount, currencyCode, 2, true)

  const handleConfirmCancel = async () => {
    if (!userId || !authToken) {
      setError('Authentication required. Please refresh and try again.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await investServices.updateOpportunityInvestment(
        {
          investmentId,
          projectId: opportunityId,
          userId: userId,
          metadata: {
            completedSteps: 2,
            bankDetailsConfirmed: false,
          },
        },
        {
          Authorization: authToken,
        },
      )

      if (response?.status === 200) {
        onCancelSuccess()
      } else {
        setError('Failed to cancel investment. Please try again or contact support.')
      }
    } catch (err) {
      console.error('Error cancelling investment:', err)
      setError('An error occurred while cancelling the investment. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <InputModal>
      <div className='cancel-investment-modal' data-testid='cancel-investment-modal'>
        <div className='cancel-investment-modal-header'>
          <H4 text='Cancel Investment' />
        </div>

        <div className='cancel-investment-modal-content'>
          <P
            text={`Are you sure you want to cancel your investment in "${projectName}"?`}
            variant='default-gray'
          />

          <div className='cancel-investment-modal-details'>
            <div className='investment-detail-row'>
              <span className='label'>Investment Amount:</span>
              <span className='value'>
                {displayDollar(investmentAmount, currencyCode, 2, true)}
              </span>
            </div>
            <div className='investment-detail-row'>
              <span className='label'>Project:</span>
              <span className='value'>{projectName}</span>
            </div>
          </div>

          <div className='cancel-investment-modal-warning'>
            <P
              text={
                refundsToWallet
                  ? `This action cannot be undone. The order will be cancelled and ${refundAmount} will be returned to your wallet. You will be able to start a new investment for this opportunity.`
                  : "This action cannot be undone. Your investment draft will be removed from 'Investments in Progress' and you will be able to start a new investment for this opportunity."
              }
              variant='default-red'
            />
          </div>

          {error && (
            <div className='cancel-investment-modal-error'>
              <P text={error} variant='default-red' />
            </div>
          )}
        </div>

        <div className='cancel-investment-modal-actions'>
          <Button
            text='No, Keep Investment'
            variant='secondary-btn'
            handleClick={onClose}
            isDisabled={isLoading}
            testId='cancel-investment-modal-keep-btn'
          />
          <Button
            text={isLoading ? 'Cancelling...' : 'Yes, Cancel Investment'}
            variant='primary-btn'
            handleClick={handleConfirmCancel}
            isDisabled={isLoading}
            testId='cancel-investment-modal-confirm-btn'
            classes={['danger-button']}
          />
        </div>
      </div>
    </InputModal>
  )
}
